import {
  Button,
  useCallbackProxy,
  useChangedRecently,
  useEffectOnValueChange,
  useHeldKey,
  usePrevious,
} from '@timelyapp/react';
import classNames from 'classnames';
import { EMPTY_ARRAY } from 'constants/defaults';
import { useStateRef } from 'hooks/useStateRef';
import { isEqual, range } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TODAY } from 'redux/modules/hours.actions';
import type { RawCapacity } from 'types/summaries';
import { scrollbarSize } from 'utils/dom-helpers';
import { fromISO } from 'utils/luxon-helpers';
import styles from './GanttChart.module.scss';
import GanttChartFloatingToolbar from './GanttChartFloatingToolbar';
import GanttChartRow from './GanttChartRow';
import type { ScrollAreaApi } from './GanttScrollArea';
import { GanttScrollArea, INITIAL_DAYS_TO_SCROLL } from './GanttScrollArea';
import type {
  GanttInteractionMode,
  GanttRowLayout,
  GanttZoomLevelCategory,
  HeaderDatePreview,
  ViewportState,
} from './gantt-internal-types';
import { calculateLayoutForRow, calculateVisibleDays, ESTIMATED_ROW_HEIGHT } from './gantt-layout';
import type {
  GanttFetchState,
  GanttRow,
  GanttTask,
  GanttTaskCopyParameters,
  GanttTasksFetchingFragment,
  GanttTooltipComponent,
} from './gantt-types';
import { DEFAULT_ZOOM_LEVEL, ZOOM_LEVELS } from './gantt-zoom';
import LeftProjectRow from './left-header/LeftProjectRow';
import LeftUserRow from './left-header/LeftUserRow';
import GanttChartDayGrid from './time-axis/GanttChartDayGrid';
import GanttChartFloatingDatePreview from './time-axis/GanttChartFloatingDatePreview';
import GanttChartMonthGrid from './time-axis/GanttChartMonthGrid';
import GanttChartWeekGrid from './time-axis/GanttChartWeekGrid';
import GanttChartYearGrid from './time-axis/GanttChartYearGrid';
import { useDataFetching } from './useDataFetching';
import type { RenderRowProps } from './useVirtualizedRows';
import { useVirtualizedRows } from './useVirtualizedRows';

export interface Props {
  height?: number;
  rows: Array<GanttRow>;
  capacityMap: Record<string, Array<RawCapacity>>;
  /**
   * When this changes, reset scrollTop
   */
  scrollTopKey?: string;
  today?: string;
  fetchStateByRowId: Record<number, GanttFetchState>;
  onFetchTasksAndHours: (
    fragments: Array<GanttTasksFetchingFragment>,
    resultingFetchStateByRowId: Record<number, GanttFetchState>
  ) => Promise<unknown>;
  onMoveTask: (
    task: GanttTask,
    row: GanttRow,
    toDates: { startDate: string; endDate: string },
    meta: { interaction: 'move' | 'resize' }
  ) => void;
  onReassignTask: (task: GanttTask, from: GanttRow, to: GanttRow) => void;
  onCreateTask: (row: GanttRow, parameters: { startDate: string; endDate: string }) => void;
  onClickTask: (task: GanttTask, row: GanttRow) => void;
  onCopyTask: (task: GanttTask, parameters: GanttTaskCopyParameters) => void;

  tooltip: GanttTooltipComponent;
  allowCreateTask: boolean;
  allowChangeTask: boolean;
  showAvailability: boolean;
  defaultWeekDays: Array<string>; // MON, TUE, WED, THU, FRI, SAT, SUN
  weekStartsOn: 'monday' | 'sunday';
  rowType: 'project' | 'assignee';
}

const GanttChart = ({
  allowChangeTask,
  allowCreateTask,
  capacityMap,
  fetchStateByRowId,
  height = 500,
  onClickTask,
  onCopyTask,
  onCreateTask,
  onFetchTasksAndHours,
  onMoveTask,
  onReassignTask,
  rows,
  scrollTopKey,
  showAvailability,
  defaultWeekDays,
  weekStartsOn,
  rowType,
  today = TODAY,
  tooltip,
}: Props) => {
  const headerColumnWidth = rowType === 'project' ? 512 : 256;
  const headerRowHeight = 70;
  const scrollAreaHeight = height - headerRowHeight;

  const [leftHeaderColumnRef, setLeftHeaderColumnRef] = useState<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [modeInState, setMode] = useState<GanttInteractionMode>('default');
  const spaceIsHeld = useHeldKey(' ' /* space */);
  const mode = spaceIsHeld ? 'pan' : modeInState;

  /**
   * Keep all these in a single state object as they must be updated together to avoid
   * issues where, for example, we change zoom or column start at a different render
   * than we change scrollLeft, causing remounts of every single task.
   */
  const viewportStateRef = useStateRef<ViewportState>(() => {
    const initialColumnStart = -INITIAL_DAYS_TO_SCROLL;
    const initialColumnEnd = INITIAL_DAYS_TO_SCROLL;
    const initialColumnCount = initialColumnEnd - initialColumnStart;
    const initialZoomLevel = DEFAULT_ZOOM_LEVEL;
    const initialZoom = ZOOM_LEVELS[initialZoomLevel];
    const initialScrollLeft =
      (initialColumnCount / 2) * initialZoom.columnWidth - initialZoom.columnWidth * 7;
    return {
      zoomLevel: initialZoomLevel,
      scrollLeft: initialScrollLeft,
      scrollTop: 0,
      columnStart: initialColumnStart,
      columnEnd: initialColumnEnd,
    };
  });
  const { zoomLevel, scrollLeft, scrollTop, columnStart, columnEnd } = viewportStateRef.getState();
  const [scrollAreaApi, scrollAreaApiRef] = useState<ScrollAreaApi>();

  // Reset scrollTop when scrollTopKey changes
  useEffectOnValueChange(scrollTopKey, () => {
    scrollAreaApi?.scrollToTop();
  });
  const zoom = ZOOM_LEVELS[zoomLevel];
  const zoomLevelCategory = zoom.view;

  const handleZoomLevelCategoryChange = useCallback(
    (zoomLevelCategory: GanttZoomLevelCategory) => {
      let newZoomLevel = ZOOM_LEVELS.findIndex(
        zoomLevel => zoomLevel.defaultForView && zoomLevel.view === zoomLevelCategory
      );
      newZoomLevel = newZoomLevel === -1 ? DEFAULT_ZOOM_LEVEL : newZoomLevel;
      scrollAreaApi?.zoomToLevel(newZoomLevel);
    },
    [scrollAreaApi]
  );

  const containerWidth = containerRef.current?.offsetWidth! || 0;
  const scrollAreaWidth = containerWidth - headerColumnWidth;

  const todayColumn = -columnStart;
  const todayLineOffset =
    -columnStart * zoom.columnWidth + (zoom.columnWidth - zoom.dayPadding) / 2;

  const [previewDates, setPreviewDates] = useState<HeaderDatePreview | null>(null);
  const handleSetPreviewDates = useCallbackProxy((newDates: HeaderDatePreview | null) => {
    if (!isEqual(newDates, previewDates)) {
      setPreviewDates(newDates);
    }
  });
  const disableTooltip = previewDates != null;

  // Construct a list of all the dates for the horizontal axis for the current column setup
  const dayColumns = useMemo(() => {
    const todayDate = fromISO(today);
    const fromOffset = (offset: number) => todayDate.plus({ days: offset });
    return range(columnStart, columnEnd + 1).map(fromOffset);
  }, [columnStart, columnEnd, today]);
  const rowStartDate = useMemo(() => dayColumns[0].toISODate(), [dayColumns]);

  const rowCount = rows.length;

  const getLayoutByRow = useMemo(() => {
    // We request and cache the layout by row, so that we don't
    // calculate the layout for non-visible rows until needed.
    const cache: Record<number, GanttRowLayout> = {};
    return (index: number) => {
      return (cache[index] ??= calculateLayoutForRow(rows[index], {
        showAvailability,
      }));
    };
  }, [rows, showAvailability]);

  const layoutMeta = useMemo(
    () => ({
      zoom,
      startDate: rowStartDate,
    }),
    [rowStartDate, zoom]
  );

  const getRowHeight = useCallback(
    ({ index }: { index: number }) => getLayoutByRow(index).rowHeight,
    [getLayoutByRow]
  );

  useEffectOnValueChange(scrollTop, () => {
    if (leftHeaderColumnRef) {
      leftHeaderColumnRef.scrollTop = scrollTop;
    }
  });

  const todayLine = useMemo(
    () => <div className={styles.todayLine} style={{ left: todayLineOffset }} />,
    [todayLineOffset]
  );

  const showMouseOverPreview = zoom.view === 'day';
  const renderRow = useCallback(
    ({ rowIndex, key, style }: RenderRowProps) => {
      const row = rows[rowIndex];
      const layout = getLayoutByRow(rowIndex);
      const isRowForDisabledProject = row.type === 'project' && row.projectArchived;
      return (
        <div
          key={key}
          style={style}
          className={classNames(styles.row, { [styles.lastRow]: rowIndex >= rows.length - 1 })}
        >
          <GanttChartRow
            layout={layout}
            ganttRow={row}
            height={style.height}
            showMouseOverPreview={showMouseOverPreview}
            scrollLeft={scrollLeft}
            scrollAreaWidth={scrollAreaWidth}
            onSetPreviewDates={handleSetPreviewDates}
            onMoveTask={onMoveTask}
            onReassignTask={onReassignTask}
            onCreateTask={onCreateTask}
            onClickTask={onClickTask}
            onCopyTask={onCopyTask}
            layoutMeta={layoutMeta}
            todayLine={todayLine}
            capacities={capacityMap[row.type === 'user' ? (row.userId ?? -1) : -1] ?? EMPTY_ARRAY}
            tooltip={tooltip}
            disableTooltip={disableTooltip}
            allowCreateTask={allowCreateTask && !isRowForDisabledProject}
            allowChangeTask={allowChangeTask}
            showAvailability={showAvailability}
            defaultWeekDays={defaultWeekDays}
            weekStartsOn={weekStartsOn}
          />
        </div>
      );
    },
    [
      allowChangeTask,
      allowCreateTask,
      capacityMap,
      defaultWeekDays,
      disableTooltip,
      getLayoutByRow,
      handleSetPreviewDates,
      layoutMeta,
      onClickTask,
      onCopyTask,
      onCreateTask,
      onMoveTask,
      onReassignTask,
      rows,
      scrollAreaWidth,
      scrollLeft,
      showAvailability,
      showMouseOverPreview,
      todayLine,
      tooltip,
      weekStartsOn,
    ]
  );

  const renderLeftHeaderRow = useCallback(
    ({ rowIndex, key, style }: RenderRowProps) => {
      const row = rows[rowIndex];
      const previousRow = rows[rowIndex - 1];
      const nextRow = rows[rowIndex + 1];
      const capacityHistory =
        capacityMap[row.type === 'user' ? (row.userId ?? -1) : -1] ?? EMPTY_ARRAY;
      const rowHeight = getRowHeight({ index: rowIndex });
      const displayClient =
        rowIndex === 0 ||
        scrollTop >= style.top ||
        (row.type === 'project' &&
          previousRow.type === 'project' &&
          previousRow.clientName !== row.clientName);
      const overflowClientToNext =
        row.type === 'project' &&
        nextRow?.type === 'project' &&
        nextRow.clientName === row.clientName;

      switch (row.type) {
        case 'project':
          return (
            <LeftProjectRow
              key={row.projectId ?? key}
              row={row}
              style={style}
              isLastRow={rowIndex >= rows.length - 1}
              rowHeight={rowHeight}
              scrollTop={scrollTop}
              overflowClientToNext={overflowClientToNext}
              displayClient={displayClient}
            />
          );
        case 'user':
          return (
            <LeftUserRow
              key={row.userId ?? key}
              row={row}
              style={style}
              isLastRow={rowIndex >= rows.length - 1}
              capacityHistory={capacityHistory}
              showAvailability={showAvailability}
              rowHeight={rowHeight}
              scrollTop={scrollTop}
            />
          );
      }
    },
    [rows, capacityMap, showAvailability, getRowHeight, scrollTop]
  );

  const virtualizedRows = useVirtualizedRows({
    scrollTop,
    height: scrollAreaHeight,
    getRowHeight,
    rowCount,
    estimatedRowHeight: ESTIMATED_ROW_HEIGHT,
  });

  // When row height changes, we update scrollTop so that we
  // maintain the same row on the top of the viewport
  const previousRowCount = usePrevious(rowCount);
  const previousVirtualizedRows = usePrevious(virtualizedRows);
  useEffect(() => {
    if (previousRowCount !== rowCount) {
      return;
    }
    if (previousVirtualizedRows.totalScrollHeight === virtualizedRows.totalScrollHeight) {
      return;
    }
    const firstRowBefore = previousVirtualizedRows.rowIndexes[0];
    if (firstRowBefore == null) {
      return;
    }
    const firstRowOffset = scrollTop - previousVirtualizedRows.getRowTop(firstRowBefore);
    const newScrollTop = virtualizedRows.getRowTop(firstRowBefore) + firstRowOffset;
    scrollAreaApi?.scrollToTop(newScrollTop);
  });

  const visibleDays = useMemo(
    () => calculateVisibleDays(scrollLeft, scrollAreaWidth, { startDate: rowStartDate, zoom }),
    [rowStartDate, zoom, scrollLeft, scrollAreaWidth]
  );

  const visibleRows = useMemo(
    () => virtualizedRows.rowIndexes.map(index => rows[index]),
    [rows, virtualizedRows.rowIndexes]
  );

  const { isFetchingTasksAndHours } = useDataFetching({
    fetchStateByRowId,
    visibleDays,
    visibleRows,
    onFetchTasksAndHours,
  });

  const todayVisibleLeft = todayLineOffset > scrollLeft - headerColumnWidth - zoom.columnWidth / 2;
  const todayVisibleRight = todayLineOffset < scrollLeft + scrollAreaWidth + zoom.columnWidth / 2;
  const todayVisible = todayVisibleLeft && todayVisibleRight;
  let goToTodayButton: React.ReactElement | null = null;
  const handleGoToToday = useCallback(() => {
    scrollAreaApi?.scrollToColumn(todayColumn);
  }, [todayColumn, scrollAreaApi]);
  if (!todayVisible) {
    const side = todayVisibleLeft ? 'right' : 'left';
    const label = side === 'right' ? 'Today →' : '← Today';
    goToTodayButton = (
      <div className={classNames(styles.goToTodayButton, { [styles.right]: todayVisibleLeft })}>
        <Button priority="medium" size="small" onClick={handleGoToToday}>
          {label}
        </Button>
      </div>
    );
  }

  const showDaysHeader = zoom.view === 'day';
  const isResizing = useChangedRecently(rowStartDate, 1000);
  const isZooming = useChangedRecently(zoomLevel, 100);
  const isChangingAvailability = useChangedRecently(showAvailability, 100);
  const disableAnimations = isZooming || isResizing || isChangingAvailability;
  // force a re-render if the zoom view or level changes
  const viewKey = zoom.view + zoomLevel;

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, {
        [styles.disableAnimations]: disableAnimations,
        [styles.isFetching]: isFetchingTasksAndHours,
      })}
    >
      <div
        className={classNames(styles.topHeader, {
          [styles.castShadow]: scrollTop > 0,
        })}
        style={{
          height: headerRowHeight,
        }}
      >
        <div className={styles.topHeaderFlexContainer}>
          {zoom.view === 'month' && (
            <GanttChartYearGrid
              key={viewKey}
              dayColumns={dayColumns}
              zoom={zoom}
              scrollLeft={scrollLeft - headerColumnWidth}
              width={containerWidth}
            />
          )}
          <GanttChartMonthGrid
            key={'GanttChartMonthGrid' + viewKey}
            dayColumns={dayColumns}
            zoom={zoom}
            scrollLeft={scrollLeft - headerColumnWidth}
            width={containerWidth}
          />
          {zoom.view === 'week' && (
            <GanttChartWeekGrid
              key={viewKey}
              dayColumns={dayColumns}
              zoom={zoom}
              scrollLeft={scrollLeft - headerColumnWidth}
              width={containerWidth}
            />
          )}
          {showDaysHeader && (
            <GanttChartDayGrid
              key={viewKey}
              dayColumns={dayColumns}
              todayIndex={todayColumn}
              previewDates={previewDates}
              zoom={zoom}
              scrollLeft={scrollLeft - headerColumnWidth}
              width={containerWidth}
            />
          )}
        </div>
        {goToTodayButton}
      </div>
      <div className={styles.floatingHeaderBorderContainer}>
        <div className={styles.floatingHeaderBorder}>
          {todayVisible && (
            <div
              className={classNames(styles.todayLine, { [styles.pin]: !showDaysHeader })}
              style={{ left: todayLineOffset - scrollLeft + headerColumnWidth }}
            />
          )}
          {!showDaysHeader && (
            <GanttChartFloatingDatePreview
              previewDates={previewDates}
              zoom={zoom}
              scrollLeft={scrollLeft - headerColumnWidth}
              width={containerWidth + headerColumnWidth}
              rowStartDate={rowStartDate}
            />
          )}
        </div>
      </div>
      <div
        className={styles.leftColumns}
        style={{
          width: headerColumnWidth,
          top: headerRowHeight,
          height: scrollAreaHeight,
        }}
      >
        <div
          className={styles.leftColumnContainer}
          ref={setLeftHeaderColumnRef}
          style={{
            height: scrollAreaHeight,
            width: headerColumnWidth,
            overflow: 'hidden',
          }}
        >
          <div
            className={styles.leftHeaderRow}
            style={{
              width: headerColumnWidth,
              height: virtualizedRows.totalScrollHeight + scrollbarSize(),
            }}
          >
            {virtualizedRows.render(renderLeftHeaderRow)}
          </div>
        </div>
      </div>
      <div className={styles.rightColumnContainer} style={{ paddingLeft: headerColumnWidth }}>
        <GanttScrollArea
          scrollAreaApiRef={scrollAreaApiRef}
          viewportStateRef={viewportStateRef}
          height={scrollAreaHeight}
          width={scrollAreaWidth}
          rowStartDate={rowStartDate}
          totalScrollHeight={virtualizedRows.totalScrollHeight}
          todayLine={todayLine}
          mode={mode}
          defaultWeekDays={defaultWeekDays}
        >
          {virtualizedRows.render(renderRow)}
        </GanttScrollArea>
        <GanttChartFloatingToolbar
          zoomLevelCategory={zoomLevelCategory}
          onZoomLevelCategoryChange={handleZoomLevelCategoryChange}
          mode={mode}
          onSetMode={setMode}
        />
      </div>
    </div>
  );
};

export default GanttChart;
